import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { PassiveBorderTransportMeansType, TransportEquipmentType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { excludeDeleted, sortBySequenceNumber } from 'shared/lib/common-util'
import {
  blankBuyer,
  blankConsignee,
  blankConsignor,
  blankPlaceOfAcceptance,
  blankPlaceOfDelivery,
  blankSeller,
} from 'features/declaration/models/blankItems'
import {
  parsePlaceOfAcceptance,
  parsePlaceOfDelivery,
  toCreateOrUpdatePlaceRequest,
} from 'features/declaration/place/models/mapper'
import {
  parseActiveBorderTransportMeansResponse,
  parseCountryOfRoutingResponse,
  parseDepartureTransportMeansResponse,
  toCreateOrUpdateCountryOfRoutingRequest,
  toCreateOrUpdateDepartureTransportMeansRequest,
} from 'features/declaration/consignment/models/mapper'
import { parseTransportEquipmentResponse } from 'features/declaration/transport-equipment/models/mapper'
import {
  CreateHouseConsignmentRequest,
  CreateOrUpdateActiveBorderTransportMeansRequest,
  CreateOrUpdateTransportEquipmentRequest,
  HouseConsignmentResponse,
  UpdateHouseConsignmentRequest,
} from '../../../../api/models'

import { HouseConsignmentResponseV3 } from '../../../../api/models/house-consignment-response-v3'
import { HouseConsignment, HouseConsignmentDetails, TransitionalHouseConsignment } from './types'
import {
  fromConsignmentDetailDetailedResponse,
  fromConsignmentDetailResponse, toBuyerFormValues,
  toConsigneeFormValues,
  toConsignorFormValues,
  toSellerFormValues,
  parseBuyerConsignmentDetailResponse,
  parseConsigneeConsignmentDetailResponse,
  parseConsignorConsignmentDetailResponse,
  parseSellerConsignmentDetailResponse,
} from '../../consignment-detail/models/mapper'
import {
  parseAdditionalSupplyChainActorResponse,
  toCreateOrUpdateAdditionalSupplyChainActorRequest,
} from '../../additional-supply-chain-actor/models/mapper'
import { HouseConsignmentDetailsResponseV3 } from '../../../../api/models/house-consignment-details-response-v3'
import { HouseConsignmentCreate, HouseConsignmentUpdate } from './request'
import { Document } from '../../document/models/types'
import { toPreviousDocuments, toSupportingDocuments, toTransportDocuments } from '../../document/models/modelToModel'

export function toHouseConsignmentFormValues(
  houseConsignment: HouseConsignmentDetails,
  documents: Document[],
): HouseConsignmentFormValues {
  return {
    id: houseConsignment.id,
    sequenceNumber: houseConsignment.sequenceNumber,
    grossMass: houseConsignment.grossMass,
    referenceNumberUCR: houseConsignment.referenceNumberUCR,
    securityIndicatorFromExportDeclaration: '',
    transportChargesMethodOfPayment: houseConsignment.transportChargesMethodOfPayment,
    consignor: houseConsignment.consignor ? toConsignorFormValues(houseConsignment.consignor) : blankConsignor,
    consignee: houseConsignment.consignee ? toConsigneeFormValues(houseConsignment.consignee) : blankConsignee,
    countryOfDestination: houseConsignment.countryOfDestination,
    countryOfDispatch: houseConsignment.countryOfDispatch,
    placeOfAcceptance: houseConsignment.placeOfAcceptance ? houseConsignment.placeOfAcceptance : blankPlaceOfAcceptance,
    placeOfDelivery: houseConsignment.placeOfDelivery ? houseConsignment.placeOfDelivery : blankPlaceOfDelivery,
    buyer: houseConsignment.buyer ? toBuyerFormValues(houseConsignment.buyer) : blankBuyer,
    seller: houseConsignment.seller ? toSellerFormValues(houseConsignment.seller) : blankSeller,
    containerIndicator: houseConsignment.containerIndicator ? 'true' : 'false',
    additionalSupplyChainActor: houseConsignment.additionalSupplyChainActor,
    transportDocument: toTransportDocuments(documents),
    previousDocument: toPreviousDocuments(documents),
    supportingDocument: toSupportingDocuments(documents),
    countriesOfRouting: houseConsignment.countriesOfRouting,
    departureTransportMeans: houseConsignment.departureTransportMeans.sort(sortBySequenceNumber),
    passiveBorderTransportMeans: houseConsignment.passiveBorderTransportMeans.sort(sortBySequenceNumber),
    differentCountriesOfRouting: houseConsignment.differentCountriesOfRouting,
    differentSeller: houseConsignment.differentSeller,
    differentBuyer: houseConsignment.differentBuyer,
    differentPlaceOfDelivery: houseConsignment.differentPlaceOfDelivery,
    differentPlaceOfAcceptance: houseConsignment.differentPlaceOfAcceptance,
    transportEquipment: houseConsignment.transportEquipment,
    additionalInformation: [],
    additionalReference: [],
    consignmentItem: [],
    deleted: false,
  }
}

export function toHouseConsignmentCreate(
  consignmentId: number,
  formValues: HouseConsignmentFormValues,
  consignorId: number | null,
  consigneeId: number | null,
  sellerId: number | null,
  buyerId: number | null,
): HouseConsignmentCreate {
  return {
    consignmentId,
    sequenceNumber: formValues.sequenceNumber,
    grossMass: formValues.grossMass,
    referenceNumberUCR: formValues.referenceNumberUCR,
    transportChargesMethodOfPayment: formValues.transportChargesMethodOfPayment,
    consignorId,
    consigneeId,
    countryOfDestination: formValues.countryOfDestination,
    countryOfDispatch: formValues.countryOfDispatch,
    placeOfAcceptance: formValues.placeOfAcceptance,
    placeOfDelivery: formValues.placeOfDelivery,
    buyerId,
    sellerId,
    containerIndicator: formValues.containerIndicator === 'true',
    additionalSupplyChainActor: formValues.additionalSupplyChainActor,
    countriesOfRouting: formValues.countriesOfRouting,
    departureTransportMeans: formValues.departureTransportMeans,
    passiveBorderTransportMeans: formValues.passiveBorderTransportMeans,
    differentCountriesOfRouting: formValues.differentCountriesOfRouting,
    differentSeller: formValues.differentSeller,
    differentBuyer: formValues.differentBuyer,
    differentPlaceOfDelivery: formValues.differentPlaceOfDelivery,
    differentPlaceOfAcceptance: formValues.differentPlaceOfAcceptance,
    transportEquipment: formValues.transportEquipment,
  }
}

export function toHouseConsignmentCreateDraft(
  consignmentId: number,
  sequenceNumber: number,
): HouseConsignmentCreate {
  return {
    consignmentId,
    sequenceNumber,
    grossMass: 0,
    referenceNumberUCR: '',
    transportChargesMethodOfPayment: '',
    consignorId: null,
    consigneeId: null,
    countryOfDestination: '',
    countryOfDispatch: '',
    placeOfAcceptance: null,
    placeOfDelivery: null,
    buyerId: null,
    sellerId: null,
    containerIndicator: false,
    additionalSupplyChainActor: [],
    countriesOfRouting: [],
    departureTransportMeans: [],
    passiveBorderTransportMeans: [],
    differentCountriesOfRouting: false,
    differentSeller: false,
    differentBuyer: false,
    differentPlaceOfDelivery: false,
    differentPlaceOfAcceptance: false,
    transportEquipment: [],
  }
}

export function toHouseConsignmentUpdate(
  id: number,
  formValues: HouseConsignmentFormValues,
  consignorId: number | null,
  consigneeId: number | null,
  sellerId: number | null,
  buyerId: number | null,
): HouseConsignmentUpdate {
  return {
    id,
    sequenceNumber: formValues.sequenceNumber,
    grossMass: formValues.grossMass,
    referenceNumberUCR: formValues.referenceNumberUCR,
    transportChargesMethodOfPayment: formValues.transportChargesMethodOfPayment,
    consignorId,
    consigneeId,
    countryOfDestination: formValues.countryOfDestination,
    countryOfDispatch: formValues.countryOfDispatch,
    placeOfAcceptance: formValues.placeOfAcceptance,
    placeOfDelivery: formValues.placeOfDelivery,
    buyerId,
    sellerId,
    containerIndicator: formValues.containerIndicator === 'true',
    additionalSupplyChainActor: formValues.additionalSupplyChainActor,
    countriesOfRouting: formValues.countriesOfRouting,
    departureTransportMeans: formValues.departureTransportMeans,
    passiveBorderTransportMeans: formValues.passiveBorderTransportMeans,
    differentCountriesOfRouting: formValues.differentCountriesOfRouting,
    differentSeller: formValues.differentSeller,
    differentBuyer: formValues.differentBuyer,
    differentPlaceOfDelivery: formValues.differentPlaceOfDelivery,
    differentPlaceOfAcceptance: formValues.differentPlaceOfAcceptance,
    transportEquipment: formValues.transportEquipment,
  }
}

export function toCreateOrUpdateTransportEquipmentRequest(
  equipment: TransportEquipmentType,
  index: number,
): CreateOrUpdateTransportEquipmentRequest {
  return {
    id: equipment.id,
    sequenceNumber: index,
    containerIdentificationNumber: equipment.containerIdentificationNumber,
    containerSizeAndType: equipment.containerSizeAndType,
    packedStatus: equipment.packedStatus,
    supplierType: equipment.supplierType,
    seals: equipment.seals
      .filter((item) => !item.deleted)
      .map((sealItem, sealIndex) => ({
        id: sealItem.id,
        sequenceNumber: sealIndex,
        identifier: sealItem.identifier,
      })),
  }
}

export function toCreateOrUpdateActiveBorderTransportMeansRequest(
  borderMeansItem: PassiveBorderTransportMeansType,
  index: number,
): CreateOrUpdateActiveBorderTransportMeansRequest {
  return {
    id: borderMeansItem.id,
    sequenceNumber: index,
    typeOfIdentification: borderMeansItem.typeOfIdentification,
    identificationNumber: borderMeansItem.identificationNumber,
    nationality: borderMeansItem.nationality,
    typeOfMeansOfTransport: borderMeansItem.typeOfMeansOfTransport ?? '',
  }
}

export function parseHouseConsignmentDetailsResponse(
  response: HouseConsignmentResponse,
  index: number,
): TransitionalHouseConsignment {
  return {
    id: response.id,
    countryOfDestination: response?.countryOfDestination ?? '',
    countryOfDispatch: response?.countryOfDispatch ?? '',
    grossMass: response.grossMass ?? 0,
    transportChargesMethodOfPayment: response?.transportChargesMethodOfPayment ?? '',
    containerIndicator: response?.containerIndicator === true ? 'true' : 'false',
    sequenceNumber: response.sequenceNumber ?? index,
    referenceNumberUCR: response.referenceNumberUcr ?? '',
    consignee: parseConsigneeConsignmentDetailResponse(response?.consignee),
    consignor: parseConsignorConsignmentDetailResponse(response?.consignor),
    placeOfDelivery: parsePlaceOfDelivery(response?.placeOfDelivery),
    placeOfAcceptance: parsePlaceOfAcceptance(response?.placeOfAcceptance),
    additionalSupplyChainActor: response?.additionalSupplyChainActors?.map(parseAdditionalSupplyChainActorResponse) ?? [],
    departureTransportMeans: response?.departureTransportMeans?.map(parseDepartureTransportMeansResponse) ?? [],
    seller: parseSellerConsignmentDetailResponse(response?.seller),
    buyer: parseBuyerConsignmentDetailResponse(response?.buyer),
    countriesOfRouting: response?.countryOfRoutings?.map(parseCountryOfRoutingResponse) ?? [],
    transportEquipment: response?.transportEquipments?.map(parseTransportEquipmentResponse) ?? [],
    passiveBorderTransportMeans: response?.passiveBorderTransportMeans?.map(parseActiveBorderTransportMeansResponse) ?? [],
    differentSeller: response?.differentSeller === true,
    differentBuyer: response?.differentBuyer === true,
    differentPlaceOfAcceptance: response?.differentPlaceOfAcceptance === true,
    differentPlaceOfDelivery: response?.differentPlaceOfDelivery === true,
    differentCountriesOfRouting: response?.differentCountriesOfRouting === true,
  }
}

export function toCreateHouseConsignmentRequest(data: HouseConsignmentCreate): CreateHouseConsignmentRequest {
  return {
    consignmentId: data.consignmentId,
    countryOfDispatch: data.countryOfDispatch,
    countryOfDestination: data.countryOfDestination,
    additionalSupplyChainActors: data.additionalSupplyChainActor
      .filter(excludeDeleted)
      .map(toCreateOrUpdateAdditionalSupplyChainActorRequest),
    consigneeId: data.consigneeId,
    consignorId: data.consignorId,
    placeOfDelivery: data.placeOfDelivery
      ? toCreateOrUpdatePlaceRequest(data.placeOfDelivery)
      : undefined,
    placeOfAcceptance: data.placeOfAcceptance
      ? toCreateOrUpdatePlaceRequest(data.placeOfAcceptance)
      : undefined,
    departureTransportMeans: data.departureTransportMeans.filter(excludeDeleted)
      .map(toCreateOrUpdateDepartureTransportMeansRequest),
    grossMass: data.grossMass,
    transportChargesMethodOfPayment: data.transportChargesMethodOfPayment ?? '',
    containerIndicator: data.containerIndicator,
    referenceNumberUcr: data.referenceNumberUCR,
    sequenceNumber: data.sequenceNumber,
    sellerId: data.sellerId,
    buyerId: data.buyerId,
    countryOfRoutings: data.countriesOfRouting
      .filter(excludeDeleted)
      .map(toCreateOrUpdateCountryOfRoutingRequest),
    transportEquipments: data.transportEquipment
      .filter(excludeDeleted)
      .map((equipment, index) => toCreateOrUpdateTransportEquipmentRequest(equipment, index)),
    passiveBorderTransportMeans: data.passiveBorderTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateActiveBorderTransportMeansRequest),
    differentSeller: data.differentSeller,
    differentBuyer: data.differentBuyer,
    differentPlaceOfDelivery: data.differentPlaceOfDelivery,
    differentPlaceOfAcceptance: data.differentPlaceOfAcceptance,
    differentCountriesOfRouting: data.differentCountriesOfRouting,
  }
}

export function toUpdateHouseConsignmentRequest(data: HouseConsignmentUpdate): UpdateHouseConsignmentRequest {
  return {
    id: data.id,
    countryOfDispatch: data.countryOfDispatch,
    countryOfDestination: data.countryOfDestination,
    additionalSupplyChainActors: data.additionalSupplyChainActor
      .filter(excludeDeleted)
      .map(toCreateOrUpdateAdditionalSupplyChainActorRequest),
    consigneeId: data.consigneeId,
    consignorId: data.consignorId,
    placeOfDelivery: data.placeOfDelivery
      ? toCreateOrUpdatePlaceRequest(data.placeOfDelivery)
      : undefined,
    placeOfAcceptance: data.placeOfAcceptance
      ? toCreateOrUpdatePlaceRequest(data.placeOfAcceptance)
      : undefined,
    departureTransportMeans: data.departureTransportMeans.filter(excludeDeleted)
      .map(toCreateOrUpdateDepartureTransportMeansRequest),
    grossMass: data.grossMass,
    transportChargesMethodOfPayment: data.transportChargesMethodOfPayment ?? '',
    containerIndicator: data.containerIndicator,
    referenceNumberUcr: data.referenceNumberUCR,
    sequenceNumber: data.sequenceNumber,
    sellerId: data.sellerId,
    buyerId: data.buyerId,
    countryOfRoutings: data.countriesOfRouting
      .filter(excludeDeleted)
      .map(toCreateOrUpdateCountryOfRoutingRequest),
    transportEquipments: data.transportEquipment
      .filter(excludeDeleted)
      .map((equipment, index) => toCreateOrUpdateTransportEquipmentRequest(equipment, index)),
    passiveBorderTransportMeans: data.passiveBorderTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateActiveBorderTransportMeansRequest),
    differentSeller: data.differentSeller,
    differentBuyer: data.differentBuyer,
    differentPlaceOfDelivery: data.differentPlaceOfDelivery,
    differentPlaceOfAcceptance: data.differentPlaceOfAcceptance,
    differentCountriesOfRouting: data.differentCountriesOfRouting,
  }
}

export function fromHouseConsignmentResponseV3(
  response: HouseConsignmentResponseV3,
  index: number,
): HouseConsignment {
  return {
    id: response.id,
    sequenceNumber: response.sequenceNumber ?? index,
    grossMass: response.grossMass ?? 0,
    consignee: response.consignee ? fromConsignmentDetailResponse(response.consignee, 'CONSIGNEE') : null,
    consignor: response.consignor ? fromConsignmentDetailResponse(response.consignor, 'CONSIGNOR') : null,
    seller: response.seller ? fromConsignmentDetailResponse(response.seller, 'SELLER') : null,
    buyer: response.buyer ? fromConsignmentDetailResponse(response.buyer, 'BUYER') : null,
    numberOfGoods: response.numberOfGoods ?? 0,
    invalidCommodityCode: response.invalidCommodityCode,
  }
}

export function fromHouseConsignmentDetailsResponse(
  response: HouseConsignmentDetailsResponseV3,
): HouseConsignmentDetails {
  return {
    id: response.id,
    countryOfDestination: response?.countryOfDestination ?? '',
    countryOfDispatch: response?.countryOfDispatch ?? '',
    grossMass: response.grossMass ?? 0,
    transportChargesMethodOfPayment: response?.transportChargesMethodOfPayment ?? '',
    containerIndicator: !!response?.containerIndicator,
    sequenceNumber: response.sequenceNumber,
    referenceNumberUCR: response.referenceNumberUcr ?? '',
    consignee: response?.consignee ? fromConsignmentDetailDetailedResponse(response.consignee, 'CONSIGNEE') : null,
    consignor: response?.consignor ? fromConsignmentDetailDetailedResponse(response.consignor, 'CONSIGNOR') : null,
    placeOfDelivery: parsePlaceOfDelivery(response?.placeOfDelivery),
    placeOfAcceptance: parsePlaceOfAcceptance(response?.placeOfAcceptance),
    additionalSupplyChainActor: response?.additionalSupplyChainActors?.map(parseAdditionalSupplyChainActorResponse) ?? [],
    departureTransportMeans: response?.departureTransportMeans?.map(parseDepartureTransportMeansResponse) ?? [],
    seller: response?.seller ? fromConsignmentDetailDetailedResponse(response.seller, 'SELLER') : null,
    buyer: response?.buyer ? fromConsignmentDetailDetailedResponse(response.buyer, 'BUYER') : null,
    countriesOfRouting: response?.countryOfRoutings?.map(parseCountryOfRoutingResponse) ?? [],
    transportEquipment: response?.transportEquipments?.map(parseTransportEquipmentResponse) ?? [],
    passiveBorderTransportMeans: response?.passiveBorderTransportMeans?.map(parseActiveBorderTransportMeansResponse) ?? [],
    differentSeller: response?.differentSeller === true,
    differentBuyer: response?.differentBuyer === true,
    differentPlaceOfAcceptance: response?.differentPlaceOfAcceptance === true,
    differentPlaceOfDelivery: response?.differentPlaceOfDelivery === true,
    differentCountriesOfRouting: response?.differentCountriesOfRouting === true,
  }
}

export function fromHouseConsignmentDetails(details: HouseConsignmentDetails, numberOfGoods: number): HouseConsignment {
  return {
    id: details.id,
    sequenceNumber: details.sequenceNumber,
    grossMass: details.grossMass,
    consignor: details.consignor,
    consignee: details.consignee,
    seller: details.seller,
    buyer: details.buyer,
    numberOfGoods,
    invalidCommodityCode: false,
  }
}
